import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, InputLabel, FormControl, Alert, Box, Autocomplete } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { AdSpaceCouponCodeInterface, AdSpaceInterface } from '../../../interfaces/interfaces';

interface Props {
  open: boolean;
  codeToUpdate: AdSpaceCouponCodeInterface | null;
  setCodeToUpdate: React.Dispatch<React.SetStateAction<AdSpaceCouponCodeInterface | null>>;
  adSpaces: AdSpaceInterface[];
  onClose: () => void;
  onSave: () => void;
  updateError?: string | null;
  updateSuccess?: string | null;
}

export const AdSpaceCouponCodesUpdateDialog: React.FC<Props> = ({
  open,
  codeToUpdate,
  setCodeToUpdate,
  adSpaces,
  onClose,
  onSave,
  updateError,
  updateSuccess,
}) => {
  const [validFrom, setValidFrom] = useState<Dayjs | null>(null);
  const [validUntil, setValidUntil] = useState<Dayjs | null>(null);
  const [selectedAdSpace, setSelectedAdSpace] = useState<AdSpaceInterface | null>(null);

  useEffect(() => {
    if (codeToUpdate) {
      setValidFrom(codeToUpdate.validFrom ? dayjs(codeToUpdate.validFrom) : null);
      setValidUntil(codeToUpdate.validUntil ? dayjs(codeToUpdate.validUntil) : null);
      const adSpace = adSpaces.find(as => as.id === codeToUpdate.adSpaceId);
      setSelectedAdSpace(adSpace || null);
    }
  }, [codeToUpdate, adSpaces]);

  const handleDateUpdate = useCallback(() => {
    if (codeToUpdate) {
      setCodeToUpdate({
        ...codeToUpdate,
        validFrom: validFrom?.toDate() || null,
        validUntil: validUntil?.toDate() || null,
      });
    }
  }, [codeToUpdate, validFrom, validUntil, setCodeToUpdate]);

  useEffect(() => {
    handleDateUpdate();
  }, [validFrom, validUntil, handleDateUpdate]);

  const discountTypeHelperText = codeToUpdate?.discountType === 'percentage' 
    ? 'Enter 10 for 10% discount' 
    : 'Enter actual amount (e.g., 30 for $30 off)';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Update AdSpace Coupon Code</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {updateError && <Alert severity="error">{updateError}</Alert>}
        {updateSuccess && <Alert severity="success">{updateSuccess}</Alert>}
        
        <Autocomplete
          options={adSpaces}
          getOptionLabel={(option) => `${option.id} - ${option.geoName}`}
          value={selectedAdSpace}
          onChange={(_, value) => {
            setSelectedAdSpace(value);
            setCodeToUpdate(c => c ? { ...c, adSpaceId: value?.id || 0 } : c);
          }}
          renderInput={(params) => <TextField {...params} label="Ad Space" required />}
        />

        <TextField
          label="Code"
          value={codeToUpdate?.code ?? ''}
          onChange={e => setCodeToUpdate(c => c ? { ...c, code: e.target.value.toUpperCase() } : c)}
          fullWidth
          margin="dense"
          required
        />

        <FormControl fullWidth margin="dense">
          <InputLabel id="discount-type-update-label">Discount Type</InputLabel>
          <Select
            labelId="discount-type-update-label"
            value={codeToUpdate?.discountType ?? 'percentage'}
            label="Discount Type"
            onChange={e => setCodeToUpdate(c => c ? { ...c, discountType: e.target.value as 'percentage' | 'fixed_amount' } : c)}
          >
            <MenuItem value="percentage">Percentage</MenuItem>
            <MenuItem value="fixed_amount">Fixed Amount</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Discount Value"
          type="number"
          value={codeToUpdate?.discountValue ?? 0}
          onChange={e => setCodeToUpdate(c => c ? { ...c, discountValue: parseFloat(e.target.value) } : c)}
          fullWidth
          margin="dense"
          required
          helperText={discountTypeHelperText}
        />

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label="Valid From"
              value={validFrom}
              onChange={(newValue) => setValidFrom(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="Valid Until"
              value={validUntil}
              onChange={(newValue) => setValidUntil(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        </LocalizationProvider>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Max Uses"
            type="number"
            value={codeToUpdate?.maxUses ?? ''}
            onChange={e => setCodeToUpdate(c => c ? { ...c, maxUses: e.target.value ? parseInt(e.target.value) : null } : c)}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Max Uses Per User"
            type="number"
            value={codeToUpdate?.maxUsesPerUser ?? 1}
            onChange={e => setCodeToUpdate(c => c ? { ...c, maxUsesPerUser: parseInt(e.target.value) } : c)}
            fullWidth
            margin="dense"
          />
        </Box>

        <TextField
          label="Minimum Purchase Amount"
          type="number"
          value={codeToUpdate?.minimumPurchaseAmount ?? ''}
          onChange={e => setCodeToUpdate(c => c ? { ...c, minimumPurchaseAmount: e.target.value ? parseFloat(e.target.value) : null } : c)}
          fullWidth
          margin="dense"
        />

        <TextField
          label="Description"
          value={codeToUpdate?.description ?? ''}
          onChange={e => setCodeToUpdate(c => c ? { ...c, description: e.target.value } : c)}
          fullWidth
          margin="dense"
          multiline
          rows={2}
        />

        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="active-update-label">Active</InputLabel>
            <Select
              labelId="active-update-label"
              value={codeToUpdate?.active !== undefined ? (codeToUpdate.active ? 'yes' : 'no') : 'yes'}
              label="Active"
              onChange={e => setCodeToUpdate(c => c ? { ...c, active: e.target.value === 'yes' } : c)}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="dense">
            <InputLabel id="combinable-update-label">Combinable</InputLabel>
            <Select
              labelId="combinable-update-label"
              value={codeToUpdate?.combinableWithOthers !== undefined ? (codeToUpdate.combinableWithOthers ? 'yes' : 'no') : 'yes'}
              label="Combinable"
              onChange={e => setCodeToUpdate(c => c ? { ...c, combinableWithOthers: e.target.value === 'yes' } : c)}
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="primary" onClick={onSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};
